import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AcceptInvitationStepBase } from '../../shared/accept-invitation-step-base/accept-invitation-step-base';
import usStates from '../../shared/data/us-states.json';
import { ActivatedRoute } from '@angular/router';
import { inject } from '@angular/core';
import { Owner } from '../../models/owner.model';

@Component({
    selector: 'app-additional-owner-address',
    templateUrl: './additional-owner-address.component.html',
    styleUrls: ['./additional-owner-address.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdditionalOwnerAddressComponent extends AcceptInvitationStepBase implements OnInit {
  public override readonly form: FormGroup;
  public override readonly storageKey = 'additionalOwnerAddress';
  public override readonly nextUrl = 'additional-owners';
  public override readonly previousUrl = 'additional-owner-info';

  // US States array for the select dropdown
  public readonly usStates = usStates;
  private ownerId: string | null = null;

  constructor(private fb: FormBuilder) {
    super();
    this.form = this.fb.group({
      streetAddress: ['', Validators.required],
      suiteUnitOffice: [''], // Optional field
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', [Validators.required, Validators.pattern('^[0-9]{5}$')]]
    });
  }

  public override async ngOnInit(): Promise<void> {
    // Wait for the server-side draft to hydrate before reading saved owner data.
    await this.dataStorageService.ready;
    // Get owner ID from route if it exists
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.ownerId = id;
        this.loadOwnerAddressData(id);
      }
    });

    // On form changes, persist form data
    this.form.valueChanges.subscribe(() => {
      this.saveFormData();
    });
  }

  private loadOwnerAddressData(id: string) {
    const ownerData: Owner | null = this.dataStorageService.getItem(`owner_${id}`);
    if (ownerData) {
      // Extract only address properties
      const { streetAddress, suiteUnitOffice, city, state, zipCode } = ownerData;
      if (streetAddress) {
        this.form.patchValue({ streetAddress, suiteUnitOffice, city, state, zipCode });
      }
    }
  }

  public override async continue() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.ownerId) {
      this.saveFormData();
      // Persist to the server draft immediately on navigation.
      await this.dataStorageService.flush();

      // Navigate back to the list with the owner ID, relative to the parent route
      await this.router.navigate([this.nextUrl], {
        queryParams: { ownerId: this.ownerId },
        relativeTo: this.route.parent
      });
    }
  }

  public override async goBack() {
    if (this.ownerId) {
      await this.router.navigate([this.previousUrl, this.ownerId], { 
        relativeTo: this.route.parent
      });
    } else {
      await super.goBack();
    }
  }

  // Allow only number keys in zip code field
  public onlyNumberKey(event: KeyboardEvent): boolean {
    // Allow only digits 0-9, backspace, delete, tab, and arrow keys
    const allowed = [
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'
    ];
    return allowed.includes(event.key);
  }

  private saveFormData() {
    // Get current owner data
    const ownerData = this.dataStorageService.getItem(`owner_${this.ownerId}`);

    if (ownerData) {
      // Merge with new address data
      const updatedOwner = {
        ...ownerData,
        ...this.form.value
      };

      // Save updated data
      this.dataStorageService.setItem(`owner_${this.ownerId}`, updatedOwner);
    }
  }
}